/**
 * TaskBoard — Kanban board with native HTML5 drag-and-drop.
 *
 * Why native DnD instead of @dnd-kit:
 *   - Zero extra dependency (CLAUDE.md "DnD は @dnd-kit か CSS だけで実装")
 *   - Sufficient for 4 columns × N cards
 *   - Mobile fallback: tap chip → status select (column move)
 *
 * Light theme only.
 */
import { useMemo, useState } from "react";

import {
  formatDate,
  isOverdue,
  priorityChipClass,
} from "./format";
import {
  STATUS_LABELS_JA,
  TASK_STATUSES,
} from "./types";
import type { KanbanView, Task, TaskStatus, UpdateTaskInput } from "./types";

export interface TaskBoardProps {
  view: KanbanView | null;
  onMove?: (taskId: string, target: TaskStatus) => void;
  onUpdate?: (id: string, patch: UpdateTaskInput) => void;
  onSelect?: (task: Task) => void;
  canMoveTask?: (task: Task) => boolean;
  members?: { id: string; displayName: string }[];
}

const COLUMN_HEADER: Record<
  TaskStatus,
  { dot: string; bar: string; bg: string }
> = {
  todo: {
    dot: "bg-slate-400",
    bar: "bg-slate-300",
    bg: "from-slate-50/50",
  },
  doing: {
    dot: "bg-amber-500",
    bar: "bg-amber-300",
    bg: "from-amber-50/40",
  },
  review: {
    dot: "bg-purple-500",
    bar: "bg-purple-300",
    bg: "from-purple-50/40",
  },
  done: {
    dot: "bg-emerald-500",
    bar: "bg-emerald-300",
    bg: "from-emerald-50/40",
  },
};

export function TaskBoard({ view, onMove, onUpdate, onSelect, canMoveTask, members = [] }: TaskBoardProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverColumn, setHoverColumn] = useState<TaskStatus | null>(null);

  const columns = useMemo(() => {
    if (view) return view.columns;
    return TASK_STATUSES.map((status) => ({
      status,
      title: STATUS_LABELS_JA[status],
      tasks: [] as Task[],
    }));
  }, [view]);

  return (
    <div
      className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4"
      data-testid="task-board"
    >
      {columns.map((col) => {
        const meta = COLUMN_HEADER[col.status];
        const hovering = hoverColumn === col.status;
        return (
          <section
            key={col.status}
            aria-label={`${col.title} 列`}
            data-status={col.status}
            data-hover={hovering ? "true" : undefined}
            onDragOver={(e) => {
              e.preventDefault();
              setHoverColumn(col.status);
            }}
            onDragLeave={() => setHoverColumn(null)}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData("text/task-id") || draggingId;
              setHoverColumn(null);
              setDraggingId(null);
              if (!id) return;
              const moving = columns
                .flatMap((c) => c.tasks)
                .find((t) => t.id === id);
              if (!moving || moving.status === col.status || (canMoveTask && !canMoveTask(moving))) return;
              onMove?.(id, col.status);
            }}
            className={
              "flex min-h-[60vh] flex-col overflow-hidden rounded-md bg-canvas shadow-sm ring-1 ring-border-subtle transition " +
              (hovering ? "ring-2 ring-brand-500 ring-offset-1" : "")
            }
          >
            <header className="flex items-center justify-between gap-2 px-3 pt-3">
              <span className="inline-flex items-center gap-2">
                <span className={`inline-block h-2 w-2 rounded-full ${meta.dot}`} />
                <h3 className="text-sm font-semibold text-fg-primary">
                  {col.title}
                </h3>
                <span className="rounded-full bg-surface-raised px-2 py-0.5 text-[11px] text-fg-muted">
                  {col.tasks.length}
                </span>
              </span>
            </header>
            <div className={`mt-2 h-0.5 ${meta.bar}`} />
            <ul
              className={`flex flex-1 flex-col gap-2 bg-gradient-to-b ${meta.bg} to-canvas p-3`}
            >
              {col.tasks.length === 0 ? (
                <li className="rounded-md border border-dashed border-border-subtle bg-canvas/40 p-4 text-center text-xs text-fg-muted">
                  ここにドラッグして移動
                </li>
              ) : (
                col.tasks.map((task) => (
                  <BoardCard
                    key={task.id}
                    task={task}
                    assigneeName={members.find(member => member.id === task.assignee)?.displayName || task.assignee || ""}
                    onSelect={onSelect}
                    onUpdate={!canMoveTask || canMoveTask(task) ? onUpdate : undefined}
                    movable={!canMoveTask || canMoveTask(task)}
                    onDragStart={() => setDraggingId(task.id)}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setHoverColumn(null);
                    }}
                    dragging={draggingId === task.id}
                  />
                ))
              )}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

interface BoardCardProps {
  task: Task;
  assigneeName: string;
  onSelect?: (task: Task) => void;
  onUpdate?: (id: string, patch: UpdateTaskInput) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  dragging: boolean;
  movable: boolean;
}

function BoardCard({
  task,
  assigneeName,
  onSelect,
  onUpdate,
  onDragStart,
  onDragEnd,
  dragging,
  movable,
}: BoardCardProps) {
  const overdue = isOverdue(task);
  return (
    <li
      draggable={movable}
      data-testid="task-card"
      data-task-id={task.id}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/task-id", task.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={
        "group cursor-grab rounded-md bg-canvas p-3 shadow-sm ring-1 ring-border-subtle transition active:cursor-grabbing " +
        "hover:-translate-y-0.5 hover:shadow-md hover:ring-border-default " +
        (dragging ? "opacity-60 ring-brand-500" : "")
      }
    >
      <button
        type="button"
        onClick={() => onSelect?.(task)}
        className="flex w-full flex-col items-start gap-1.5 text-left"
      >
        <span className="flex flex-wrap items-center gap-1.5">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${priorityChipClass(task.priority)}`}
          >
            {task.priority}
          </span>
          {task.parent_task_id && (
            <span className="rounded-full bg-surface-raised px-1.5 py-0.5 text-[10px] text-fg-muted">
              SUB
            </span>
          )}
          {task.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded bg-surface-base px-1.5 py-0.5 text-[10px] text-fg-secondary ring-1 ring-border-subtle"
            >
              #{tag}
            </span>
          ))}
        </span>
        <span className="text-sm font-medium leading-snug text-fg-primary">
          {task.title}
        </span>
        <span className="flex max-w-full flex-wrap items-center gap-2 text-xs text-fg-muted">
          {task.assignee && (
            <span className="inline-flex max-w-full items-center gap-1" title={assigneeName}>
              <span
                aria-hidden
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[9px] font-semibold text-brand-700"
              >
                {assigneeName.slice(0, 1).toUpperCase()}
              </span>
              <span className="truncate">@{assigneeName}</span>
            </span>
          )}
          <span
            className={
              overdue
                ? "inline-flex items-center gap-1 text-danger"
                : "inline-flex items-center gap-1"
            }
          >
            {overdue && <span aria-hidden>⚠</span>}
            {formatDate(task.due_at)}
          </span>
        </span>
      </button>
      {/* Touch / a11y fallback for users who can't drag */}
      <select
        aria-label={`${task.title} のステータスを変更`}
        disabled={!onUpdate}
        className="mt-2 w-full rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-fg-secondary opacity-0 transition focus:opacity-100 group-hover:opacity-100"
        value={task.status}
        onChange={(e) =>
          onUpdate?.(task.id, { status: e.target.value as TaskStatus })
        }
      >
        {TASK_STATUSES.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABELS_JA[s]}
          </option>
        ))}
      </select>
    </li>
  );
}
